/**
 * Client-Side Automatic WebP Image Optimizer
 * Converts any image format (PNG, JPG, JPEG, BMP, etc.) into high-efficiency modern WebP format
 * right in the browser before transmission, ensuring 100% zero-server-overhead & fast uploads.
 */

export interface OptimizedImageResult {
    blob: Blob;
    dataUrl: string;
    filename: string;
    originalName: string;
    width: number;
    height: number;
    sizeBytes: number;
    originalSizeBytes: number;
    savingsPercent: number;
    mimeType: 'image/webp';
}

export interface ConvertOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number; // 0.1 to 1.0 (default 0.85)
}

export function formatBytes(bytes: number, decimals = 1): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export async function convertToWebP(
    file: File,
    options: ConvertOptions = {}
): Promise<OptimizedImageResult> {
    const { maxWidth = 1920, maxHeight = 1920, quality = 0.85 } = options;

    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { naturalWidth: width, naturalHeight: height } = img;

            // Clamping dimensions proportionally if too large
            if (width > maxWidth || height > maxHeight) {
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas 2D context not available'));
                return;
            }

            // High quality image smoothing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);

            // Clean slugified filename with .webp extension
            const baseName = file.name
                .replace(/\.[^/.]+$/, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
            const timestamp = Date.now().toString(36);
            const webpFilename = `${baseName || 'image'}-${timestamp}.webp`;

            const dataUrl = canvas.toDataURL('image/webp', quality);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to convert image to WebP blob'));
                        return;
                    }

                    const sizeBytes = blob.size;
                    const originalSizeBytes = file.size;
                    const savingsPercent = Math.max(
                        0,
                        Math.round(((originalSizeBytes - sizeBytes) / originalSizeBytes) * 100)
                    );

                    resolve({
                        blob,
                        dataUrl,
                        filename: webpFilename,
                        originalName: file.name,
                        width,
                        height,
                        sizeBytes,
                        originalSizeBytes,
                        savingsPercent,
                        mimeType: 'image/webp',
                    });
                },
                'image/webp',
                quality
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error(`Failed to load image file: ${file.name}`));
        };

        img.src = objectUrl;
    });
}
